import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiWaveDecision } from "@/generated/models/ApiWaveDecision";
import type { ApiWaveDecisionsPage } from "@/generated/models/ApiWaveDecisionsPage";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { prepareWaveDecisionPoint } from "@/helpers/waves/wave-decision.helpers";
interface UseWaveDecisionsProps {
  readonly waveId: string;
  readonly enabled?: boolean | undefined;
  readonly loadAllPages?: boolean | undefined;
  readonly pageSize?: number | undefined;
}
const DEFAULT_PAGE = 1;
const DEFAULT_WAVE_DECISIONS_PAGE_SIZE = 100;
export const FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE = 2000;
const DEFAULT_STALE_TIME = 60000;
const getValidPageSize = (pageSize: number | undefined): number => {
  if (typeof pageSize === "number" && Number.isFinite(pageSize) && pageSize > 0) {
    return Math.min(Math.floor(pageSize), FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE);
  }
  return DEFAULT_WAVE_DECISIONS_PAGE_SIZE;
};
export function useWaveDecisions(t0) {
  const $ = _c(34);
  const {
    waveId,
    enabled: t1,
    loadAllPages: t2,
    pageSize
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const loadAllPages = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== pageSize) {
    t3 = getValidPageSize(pageSize);
    $[0] = pageSize;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const resolvedPageSize = t3;
  let t4;
  let t5;
  if ($[2] !== resolvedPageSize || $[3] !== waveId) {
    t4 = [QueryKey.WAVE_DECISIONS, {
      waveId,
      pageSize: resolvedPageSize
    }];
    t5 = async t6 => {
      const {
        pageParam
      } = t6;
      const currentPage = pageParam ?? DEFAULT_PAGE;
      return await commonApiFetch({
        endpoint: `waves/${waveId}/decisions`,
        params: {
          sort_direction: "DESC",
          sort: "decision_time",
          page: currentPage.toString(),
          page_size: resolvedPageSize.toString()
        }
      });
    };
    $[2] = resolvedPageSize;
    $[3] = waveId;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] !== enabled || $[7] !== t4 || $[8] !== t5) {
    t6 = {
      queryKey: t4,
      queryFn: t5,
      initialPageParam: DEFAULT_PAGE,
      getNextPageParam: _temp,
      enabled,
      staleTime: DEFAULT_STALE_TIME
    };
    $[6] = enabled;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data,
    isError,
    error,
    refetch,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
    isFetchingNextPage
  } = useInfiniteQuery(t6);
  let t7;
  let t8;
  if ($[10] !== enabled || $[11] !== fetchNextPage || $[12] !== hasNextPage || $[13] !== isFetchNextPageError || $[14] !== isFetchingNextPage || $[15] !== loadAllPages) {
    t7 = () => {
      if (!enabled || !loadAllPages || !hasNextPage || isFetchNextPageError || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    t8 = [enabled, fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage, loadAllPages];
    $[10] = enabled;
    $[11] = fetchNextPage;
    $[12] = hasNextPage;
    $[13] = isFetchNextPageError;
    $[14] = isFetchingNextPage;
    $[15] = loadAllPages;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  data?.pages;
  let t9;
  if ($[18] !== data?.pages || $[19] !== waveId) {
    const mergedDecisionPoints = [];
    const seenDecisionTimes = new Set();
    for (const page of data?.pages ?? []) {
      for (const decisionPoint of page.data) {
        if (seenDecisionTimes.has(decisionPoint.decision_time)) {
          continue;
        }
        seenDecisionTimes.add(decisionPoint.decision_time);
        mergedDecisionPoints.push(prepareWaveDecisionPoint(decisionPoint, waveId));
      }
    }
    t9 = mergedDecisionPoints.sort(_temp2);
    $[18] = data?.pages;
    $[19] = waveId;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const decisionPoints = t9;
  const hasLoadedAllPages = Boolean(data?.pages.length) && hasNextPage === false && !isFetchingNextPage;
  const isLoadingAllPages = Boolean(enabled && (isLoading || loadAllPages && (isFetchingNextPage || !isFetchNextPageError && Boolean(hasNextPage))));
  const isLoadingAllPagesError = Boolean(enabled && loadAllPages && !hasLoadedAllPages && !isLoading && !isFetching && !isFetchingNextPage && (isError || isFetchNextPageError));
  const t10 = Boolean(hasNextPage);
  let t11;
  if ($[21] !== decisionPoints || $[22] !== error || $[23] !== fetchNextPage || $[24] !== hasLoadedAllPages || $[25] !== isError || $[26] !== isFetching || $[27] !== isFetchingNextPage || $[28] !== isLoading || $[29] !== isLoadingAllPages || $[30] !== isLoadingAllPagesError || $[31] !== refetch || $[32] !== t10) {
    t11 = {
      decisionPoints,
      isError,
      error,
      refetch,
      isFetching,
      isLoading,
      hasLoadedAllPages,
      isLoadingAllPages,
      isLoadingAllPagesError,
      fetchNextPage,
      hasNextPage: t10,
      isFetchingNextPage
    };
    $[21] = decisionPoints;
    $[22] = error;
    $[23] = fetchNextPage;
    $[24] = hasLoadedAllPages;
    $[25] = isError;
    $[26] = isFetching;
    $[27] = isFetchingNextPage;
    $[28] = isLoading;
    $[29] = isLoadingAllPages;
    $[30] = isLoadingAllPagesError;
    $[31] = refetch;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}
function _temp2(a, b) {
  return a.decision_time - b.decision_time;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}